import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth, API } from "@/App";
import axios from "axios";
import Layout from "@/components/Layout";
import { 
  Bell, 
  Megaphone, 
  Tag,
  Calendar
} from "lucide-react";

const MessagesPage = () => {
  const { token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMessages();
    markAsRead();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async () => {
    try {
      await axios.post(`${API}/messages/mark-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error("Error marking messages read:", error);
    }
  };

  const getMessageIcon = (type) => {
    switch (type) {
      case "announcement":
        return <Megaphone className="w-6 h-6 text-blue-500" />;
      case "promotion":
        return <Tag className="w-6 h-6 text-green-500" />;
      case "alert":
        return <Bell className="w-6 h-6 text-red-500" />;
      default:
        return <Bell className="w-6 h-6 text-gray-500" />;
    }
  };

  const getMessageBadge = (type) => {
    switch (type) {
      case "announcement":
        return <Badge className="bg-blue-100 text-blue-800">Announcement</Badge>;
      case "promotion":
        return <Badge className="bg-green-100 text-green-800">Partner Promotion</Badge>;
      case "alert":
        return <Badge className="bg-red-100 text-red-800">Alert</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">{type}</Badge>;
    }
  };

  return (
    <Layout title="Messages" subtitle="Stay updated with FishingFanatix news">
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner" />
        </div>
      ) : messages.length > 0 ? (
        <div className="space-y-6 max-w-3xl mx-auto">
          {messages.map((message, index) => (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className="bg-white card-shadow border-0 overflow-hidden">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[var(--bg-main)] flex items-center justify-center flex-shrink-0">
                      {getMessageIcon(message.message_type)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-lg font-bold text-[var(--deep-ocean)]">{message.title}</h3>
                        {getMessageBadge(message.message_type)}
                      </div>
                      <p className="text-[var(--river-stone)] whitespace-pre-wrap mb-4">
                        {message.content}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-[var(--river-stone)]">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(message.created_at).toLocaleDateString('en-ZA', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </div>
                        <span>•</span>
                        <span>From: {message.created_by}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      ) : (
        <Card className="bg-white card-shadow border-0 max-w-xl mx-auto">
          <CardContent className="p-12 text-center">
            <Bell className="w-16 h-16 text-[var(--river-stone)] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-[var(--deep-ocean)] mb-2">No messages yet</h3>
            <p className="text-[var(--river-stone)]">
              You'll see announcements, promotions, and alerts from FishingFanatix here.
            </p>
          </CardContent>
        </Card>
      )}
    </Layout>
  );
};

export default MessagesPage;
